import { elementColor } from "@/data/theme";
import { Info, TriangleAlert } from "lucide-react";

// Cabeçalho de página com título serifado e subtítulo.
export const PageHeader = ({ eyebrow, title, subtitle, children }) => (
  <div className="animate-fade-up">
    {eyebrow && (
      <p className="text-xs sm:text-sm font-sans uppercase tracking-[0.25em] text-gold mb-3">{eyebrow}</p>
    )}
    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold tracking-tight leading-tight text-foreground">
      {title}
    </h1>
    {subtitle && <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">{subtitle}</p>}
    {children}
  </div>
);

export const SectionTitle = ({ children, className = "" }) => (
  <h2 className={`text-2xl sm:text-3xl font-serif font-medium tracking-tight text-foreground ${className}`}>{children}</h2>
);

// Selo do elemento (apoio visual, cor conforme design).
export const ElementBadge = ({ elementKey, label, className = "" }) => {
  const c = elementColor(elementKey);
  return (
    <span
      data-testid={`element-badge-${elementKey}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${className}`}
      style={{ backgroundColor: c.soft, color: c.text }}
    >
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.primary }} />
      {label || c.label}
    </span>
  );
};

// Aviso de segurança reutilizável (parte essencial do conteúdo do guia).
export const SafetyNote = ({ children, tone = "info", className = "" }) => {
  const isWarn = tone === "warn";
  const Icon = isWarn ? TriangleAlert : Info;
  return (
    <div
      data-testid="safety-note"
      className={`flex items-start gap-3 rounded-2xl border p-4 sm:p-5 text-sm leading-relaxed ${
        isWarn
          ? "border-destructive/30 bg-destructive/5 text-destructive"
          : "border-gold/30 bg-gold/10 text-foreground"
      } ${className}`}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.75} />
      <p>{children}</p>
    </div>
  );
};

// Formata lista de páginas agrupando intervalos contínuos (ex: [12, 13, 18, 19] => "12–13, 18–19")
export const formatPageList = (pages) => {
  if (!pages) return "";
  if (!Array.isArray(pages)) return String(pages);
  if (pages.length === 0) return "";
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0];
  let prev = start;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) {
      prev = sorted[i];
    } else {
      ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
      start = sorted[i];
      prev = start;
    }
  }
  ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
  return ranges.join(", ");
};

// Etiqueta discreta de auditoria e rastreabilidade da fonte no PDF.
export const SourceTag = ({ pages, source, prefix, className = "" }) => {
  if (source) {
    const { pdfPage, printedPage, label } = source;
    if (label) {
      return (
        <span
          className={`text-[11px] font-sans uppercase tracking-wider text-muted-foreground ${className}`}
          title={pdfPage ? `Página física no PDF: ${pdfPage}` : "Fonte da informação"}
        >
          {label}
        </span>
      );
    }
    const text = printedPage
      ? `Pág. ${printedPage}`
      : pdfPage
        ? `PDF pág. ${pdfPage}`
        : "";
    const tooltip =
      pdfPage && printedPage
        ? `Página impressa ${printedPage} (PDF físico ${pdfPage})`
        : pdfPage
          ? `Página física no PDF: ${pdfPage}`
          : "";
    return (
      <span
        className={`text-[11px] font-sans uppercase tracking-wider text-muted-foreground ${className}`}
        title={tooltip}
      >
        {text}
      </span>
    );
  }

  if (!pages || !pages.length) return null;
  const isMultiple = pages.length > 1;
  const formatted = formatPageList(pages);
  const defaultPrefix = prefix || (isMultiple ? "Págs. " : "Pág. ");
  return (
    <span
      className={`text-[11px] font-sans uppercase tracking-wider text-muted-foreground ${className}`}
      title="Página de consulta / origem no guia"
    >
      {defaultPrefix}{formatted}
    </span>
  );
};
